import { useQuery } from '@tanstack/react-query';
import { httpServices } from './client';

/**
 * GET /llm/config: the limits the UI derives its text from
 * (preferences-and-export-api.md §2). The defaults are the documented
 * values, used until the response arrives or when it fails.
 */
export interface ModeLimits {
  standard: { top_files: number; passages: number };
  thinking: {
    top_files: number;
    max_total_size_mib: number;
    max_total_bytes: number;
  };
  comparison: { min_files: number; max_files: number; max_total_bytes: number };
}

export interface LlmConfig {
  bytes_per_mb: number;
  uploads: {
    accepted_formats: string[];
    accepted_extensions: string[];
    max_bytes_per_file: number;
  };
  folder_name: { min_length: number; max_length: number };
  undo_window_seconds: number;
  modes: ModeLimits;
  prompt: { max_length: number };
}

export const DEFAULT_LLM_CONFIG: LlmConfig = {
  bytes_per_mb: 1_000_000,
  uploads: {
    accepted_formats: ['pdf', 'docx', 'txt', 'md'],
    accepted_extensions: ['pdf', 'docx', 'txt', 'md', 'markdown'],
    max_bytes_per_file: 20_000_000,
  },
  folder_name: { min_length: 1, max_length: 80 },
  undo_window_seconds: 15,
  modes: {
    standard: { top_files: 20, passages: 100 },
    thinking: {
      top_files: 20,
      max_total_size_mib: 1000,
      max_total_bytes: 1_048_576_000,
    },
    comparison: { min_files: 2, max_files: 10, max_total_bytes: 20_000_000 },
  },
  prompt: { max_length: 4000 },
};

const merge = <T extends object>(base: T, value: unknown): T =>
  value && typeof value === 'object' ? { ...base, ...(value as T) } : base;

export const getLlmConfig = async (): Promise<LlmConfig> => {
  const response = await httpServices.llmService.get<{
    success: boolean;
    data?: Partial<LlmConfig> & { modes?: Partial<ModeLimits> };
  }>('/config');
  const data = response?.success ? response.data : undefined;
  const defaults = DEFAULT_LLM_CONFIG;
  return {
    ...defaults,
    ...data,
    uploads: merge(defaults.uploads, data?.uploads),
    folder_name: merge(defaults.folder_name, data?.folder_name),
    prompt: merge(defaults.prompt, data?.prompt),
    modes: {
      standard: merge(defaults.modes.standard, data?.modes?.standard),
      thinking: merge(defaults.modes.thinking, data?.modes?.thinking),
      comparison: merge(defaults.modes.comparison, data?.modes?.comparison),
    },
  };
};

/** Fetched once per session. */
export const useLlmConfig = (): LlmConfig =>
  useQuery({
    queryKey: ['llm-config'],
    queryFn: getLlmConfig,
    staleTime: Infinity,
    gcTime: Infinity,
  }).data ?? DEFAULT_LLM_CONFIG;
